import { VALIDATION_LIMITS } from '../constants/templates.js';

/**
 * Template validation utilities
 * Provides comprehensive validation functions for template data
 */

/**
 * Detect dynamic variables in text
 * @param {string} text - Text to analyze
 * @returns {string[]} Array of detected variables
 */
export const detectDynamicVariables = text => {
  if (!text || typeof text !== 'string') return [];

  const matches = text.match(/\{\{\d+\}\}/g);
  return [...new Set(matches)]; // Remove duplicates
};

/**
 * Validate template name
 * @param {string} name - Template name to validate
 * @returns {string[]} Array of validation errors
 */
export const validateTemplateName = name => {
  const errors = [];

  if (!name || typeof name !== 'string') {
    errors.push('Template name is required');
    return errors;
  }

  if (name.length > VALIDATION_LIMITS.NAME_MAX_LENGTH) {
    errors.push(
      `Template name must be less than ${VALIDATION_LIMITS.NAME_MAX_LENGTH} characters`
    );
  }

  // Check if name contains only lowercase alphanumeric characters and underscores (no spaces)
  if (!/^[a-z0-9_]+$/.test(name)) {
    errors.push(
      'Template name can only contain lowercase alphanumeric characters and underscores (no spaces)'
    );
  }

  return errors;
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether phone number is valid
 */
export const validatePhoneNumber = phone => {
  if (!phone || typeof phone !== 'string') return false;

  // International format validation (basic)
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} Whether URL is valid
 */
export const validateUrl = url => {
  if (!url || typeof url !== 'string') return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate template structure based on category
 * @param {Object} template - Template data
 * @param {string} category - Template category
 * @returns {Array} Array of validation errors
 */
export const validateTemplateStructure = (template, category) => {
  const errors = [];

  // Authentication templates cannot have header, footer, limited_time_offer, or buttons
  if (['AUTHENTICATION', 'authentication'].includes(category)) {
    if (template.header) {
      errors.push({
        field: 'header',
        message:
          'Authentication templates cannot have header, footer, limited time offer, or buttons',
      });
    }
    if (template.footer) {
      errors.push({
        field: 'footer',
        message:
          'Authentication templates cannot have header, footer, limited time offer, or buttons',
      });
    }
    if (template.limited_time_offer) {
      errors.push({
        field: 'limited_time_offer',
        message:
          'Authentication templates cannot have header, footer, limited time offer, or buttons',
      });
    }
    if (template.buttons && template.buttons.length > 0) {
      errors.push({
        field: 'buttons',
        message:
          'Authentication templates cannot have header, footer, limited time offer, or buttons',
      });
    }
  }

  // Footer and limited_time_offer are mutually exclusive
  if (template.footer && template.limited_time_offer) {
    errors.push({
      field: 'footer',
      message: 'Footer and limited time offer are mutually exclusive',
    });
  }

  return errors;
};

/**
 * Validate button component
 * @param {Object} button - Button data
 * @param {number} index - Button index for error reporting
 * @returns {Array} Array of validation errors
 */
export const validateButton = (button, index = 0) => {
  const errors = [];

  if (!button || typeof button !== 'object') {
    errors.push({
      field: `buttons[${index}]`,
      message: 'Button data is required',
    });
    return errors;
  }

  const { type, text, url, phone_number, example } = button;

  // Validate required fields based on button type
  if (['url', 'phone_number', 'quick_reply'].includes(type) && !text) {
    errors.push({
      field: `buttons[${index}].text`,
      message:
        'Button text is required for url, phone_number, and quick_reply types',
    });
  }

  if (text && text.length > VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH) {
    errors.push({
      field: `buttons[${index}].text`,
      message: `Button text must be less than ${VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH} characters`,
    });
  }

  if (type === 'url') {
    if (!url) {
      errors.push({
        field: `buttons[${index}].url`,
        message: 'URL is required for url type buttons',
      });
    } else if (!validateUrl(url)) {
      errors.push({
        field: `buttons[${index}].url`,
        message: 'Invalid URL format',
      });
    }

    // Check if URL has dynamic variables and requires example
    const variables = detectDynamicVariables(url);
    if (variables.length > 0 && !example) {
      errors.push({
        field: `buttons[${index}].example`,
        message:
          'Button example is required when URL contains dynamic variables',
      });
    }
  }

  if (type === 'phone_number') {
    if (!phone_number) {
      errors.push({
        field: `buttons[${index}].phone_number`,
        message: 'Phone number is required for phone_number type buttons',
      });
    } else if (!validatePhoneNumber(phone_number)) {
      errors.push({
        field: `buttons[${index}].phone_number`,
        message: 'Invalid phone number format',
      });
    }
  }

  if (type === 'copy_code') {
    if (!example) {
      errors.push({
        field: `buttons[${index}].example`,
        message: 'Button example is required for copy_code type buttons',
      });
    } else if (example.length > 15) {
      errors.push({
        field: `buttons[${index}].example`,
        message: 'Button example must be less than 15 characters',
      });
    }
  }

  return errors;
};

/**
 * Validate header component
 * @param {Object} header - Header data
 * @returns {Array} Array of validation errors
 */
export const validateHeader = header => {
  const errors = [];

  if (!header || typeof header !== 'object') {
    return errors;
  }

  const { format, text, url } = header;

  if (format === 'TEXT') {
    if (!text) {
      errors.push({
        field: 'header.text',
        message: 'Header text is required for TEXT format',
      });
    } else {
      if (/[\r\n]/.test(text)) {
        errors.push({
          field: 'header.text',
          message: 'Header text cannot contain new lines',
        });
      }

      if (/[*]/.test(text)) {
        errors.push({
          field: 'header.text',
          message: 'Header text cannot contain asterisks (*)',
        });
      }

      if (/\p{Extended_Pictographic}|\p{Emoji_Presentation}/u.test(text)) {
        errors.push({
          field: 'header.text',
          message: 'Header text cannot contain emojis',
        });
      }
    }
  }

  if (text && text.length > 1024) {
    errors.push({
      field: 'header.text',
      message: 'Header text must be less than 1024 characters',
    });
  }

  if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format) && !url) {
    errors.push({
      field: 'header.url',
      message: 'Header URL is required for media formats',
    });
  }

  if (url && !validateUrl(url)) {
    errors.push({
      field: 'header.url',
      message: 'Invalid URL format',
    });
  }

  return errors;
};

/**
 * Validate body component
 * @param {Object} body - Body data
 * @returns {Array} Array of validation errors
 */
export const validateBody = body => {
  const errors = [];

  if (!body || typeof body !== 'object') {
    errors.push({
      field: 'body',
      message: 'Body data is required',
    });
    return errors;
  }

  const { text } = body;

  if (!text || typeof text !== 'string') {
    errors.push({
      field: 'body.text',
      message: 'Body text is required',
    });
  } else if (text.length > 1024) {
    errors.push({
      field: 'body.text',
      message: 'Body text must be less than 1024 characters',
    });
  }

  return errors;
};

/**
 * Validate footer component
 * @param {Object} footer - Footer data
 * @returns {Array} Array of validation errors
 */
export const validateFooter = footer => {
  const errors = [];

  if (!footer || typeof footer !== 'object') {
    return errors;
  }

  const { text } = footer;

  if (text && text.length > 1024) {
    errors.push({
      field: 'footer.text',
      message: 'Footer text must be less than 1024 characters',
    });
  }

  return errors;
};

/**
 * Check if template can be edited
 * @param {Object} template - Template data
 * @returns {Object} Edit permission result
 */
export const canEditTemplate = template => {
  const result = {
    canEdit: true,
    reason: null,
    isWithin24Hours: false,
    hasReachedEditLimit: false,
    remainingEdits: 0,
  };

  if (!template) {
    result.canEdit = false;
    result.reason = 'Template not found';
    return result;
  }

  // Check if template is in a state that allows editing
  if (['APPROVED', 'REJECTED'].includes(template.status)) {
    result.canEdit = false;
    result.reason = `Cannot edit ${template.status.toLowerCase()} templates`;
    return result;
  }

  // Check 24-hour edit limit
  if (template.createdAt) {
    const createdAt = new Date(template.createdAt);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);

    if (hoursDiff > VALIDATION_LIMITS.EDIT_TIME_LIMIT_HOURS) {
      result.canEdit = false;
      result.reason = 'Template cannot be edited after 24 hours';
      return result;
    }

    result.isWithin24Hours =
      hoursDiff <= VALIDATION_LIMITS.EDIT_TIME_LIMIT_HOURS;
  }

  // Check monthly edit limit
  if (template.editCount >= VALIDATION_LIMITS.MAX_EDITS_PER_MONTH) {
    result.canEdit = false;
    result.reason = 'Monthly edit limit exceeded';
    result.hasReachedEditLimit = true;
    return result;
  }

  result.remainingEdits =
    VALIDATION_LIMITS.MAX_EDITS_PER_MONTH - (template.editCount || 0);

  return result;
};

/**
 * Validate complete template data
 * @param {Object} templateData - Complete template data
 * @returns {Array} Array of validation errors
 */
export const validateCompleteTemplate = templateData => {
  const errors = [];

  // Validate basic fields
  if (!templateData.name) {
    errors.push({
      field: 'name',
      message: 'Template name is required',
    });
  } else {
    const nameErrors = validateTemplateName(templateData.name);
    nameErrors.forEach(error => {
      errors.push({
        field: 'name',
        message: error,
      });
    });
  }

  if (!templateData.category) {
    errors.push({
      field: 'category',
      message: 'Category is required',
    });
  }

  if (!templateData.language) {
    errors.push({
      field: 'language',
      message: 'Language is required',
    });
  }

  // Validate components
  if (templateData.header) {
    errors.push(...validateHeader(templateData.header));
  }

  errors.push(...validateBody(templateData.body));

  if (templateData.footer) {
    errors.push(...validateFooter(templateData.footer));
  }

  if (templateData.buttons) {
    if (
      templateData.buttons.length > VALIDATION_LIMITS.MAX_BUTTONS_PER_TEMPLATE
    ) {
      errors.push({
        field: 'buttons',
        message: `Maximum ${VALIDATION_LIMITS.MAX_BUTTONS_PER_TEMPLATE} buttons allowed`,
      });
    }

    templateData.buttons.forEach((button, index) => {
      errors.push(...validateButton(button, index));
    });
  }

  // Validate template structure
  if (templateData.category) {
    errors.push(
      ...validateTemplateStructure(templateData, templateData.category)
    );
  }

  return errors;
};

/**
 * Sanitize template data
 * @param {Object} templateData - Template data to sanitize
 * @returns {Object} Sanitized template data
 */
export const sanitizeTemplateData = templateData => {
  const sanitized = { ...templateData };

  // Trim string fields
  if (sanitized.name) {
    sanitized.name = sanitized.name.trim();
  }

  if (sanitized.body?.text) {
    sanitized.body.text = sanitized.body.text.trim();
  }

  if (sanitized.header?.text) {
    sanitized.header.text = sanitized.header.text.trim();
  }

  if (sanitized.footer?.text) {
    sanitized.footer.text = sanitized.footer.text.trim();
  }

  // Sanitize buttons
  if (sanitized.buttons) {
    sanitized.buttons = sanitized.buttons.map(button => ({
      ...button,
      text: button.text?.trim(),
      url: button.url?.trim(),
      phone_number: button.phone_number?.trim(),
      example: button.example?.trim(),
    }));
  }

  return sanitized;
};

export default {
  detectDynamicVariables,
  validateTemplateName,
  validatePhoneNumber,
  validateUrl,
  validateTemplateStructure,
  validateButton,
  validateHeader,
  validateBody,
  validateFooter,
  canEditTemplate,
  validateCompleteTemplate,
  sanitizeTemplateData,
};
